import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Measure from "react-measure";
import {
	DataGrid,
	ProgressIndicator
} from "rhombus-react";

import { CreativesSocketListener } from "hooks/useCreativesSocket";

import { Creatives } from "reducers/Creatives";

import { CREATIVE_COLUMNS } from "widgets/grid/columns/Creatives";
import { useGridScrollUtils } from "utils/DataGrid";
import { CreativeRowRenderer } from "utils/columns/LockedRow";
import { MIN_GRID_HEIGHT, DEFAULT_ROW_HEIGHT } from "utils/constants";
import { getLibraryAutoClassificationEnabled, getManualClassificationEnabled } from "selectors/Intelligence";

function mapCreatives(creatives, currentSelection, activeCreativeId){
	const activeIdString = activeCreativeId?.toString();

	return creatives.map((creative) => {
		const {
			id,
		} = creative;

		const totalFileSize = creative?.totalFileSize?.totalSize;

		return {
			...creative,
			totalFileSize,
			actions: id,
			// TODO: is this the best way to figure this out?  Maybe if we upgrade the grid we can use it's built in functionality
			isSelected: currentSelection.indexOf(creative.id) > -1,
			isActive:  activeIdString === creative.id.toString(),
		};
	});
}

function filterVisibleColumns(props = {}) {
	const { isLibraryAutoClassificationEnabled, isManualClassificationEnabled } = props;

	if (isLibraryAutoClassificationEnabled && isManualClassificationEnabled) {
		return CREATIVE_COLUMNS;
	}

	return CREATIVE_COLUMNS.filter((column) => {
		if(column.key === "autoClassificationStatus") {
			return isLibraryAutoClassificationEnabled;
		} else if(column.key === "labelsCnt") {
			return isManualClassificationEnabled;
		}

		return true;
	});
}

export default function Grid({ creatives=[], libraryId, sort="", isLoading }) {
	const dataGridRef = useGridScrollUtils([creatives])
		, dispatch = useDispatch()
		, [sortColumn, sortDirection] = sort.split(":")
		;
	const currentSelection = useSelector(state => state.selected.creatives);
	const activeCreativeId = useSelector(state => state.creatives.activeCreativeId);
	const isManualClassificationEnabled = useSelector(getManualClassificationEnabled);
	const isLibraryAutoClassificationEnabled = useSelector(getLibraryAutoClassificationEnabled);

	const augmentedCreatives = useMemo(() => {
		return mapCreatives(creatives, currentSelection, activeCreativeId);
	}, [creatives, currentSelection, activeCreativeId]);

	const visibleColumns = useMemo(() => {
		const visibleProps = { isLibraryAutoClassificationEnabled, isManualClassificationEnabled };
		return filterVisibleColumns(visibleProps);
	}, [isLibraryAutoClassificationEnabled, isManualClassificationEnabled]);

	function getCreativeRow(rowID) {
		return augmentedCreatives[rowID];
	}

	const dataGridProps={
		columns: visibleColumns,
		rowGetter: getCreativeRow,
		rowsCount: creatives.length,
		rowRenderer: CreativeRowRenderer,
		rowHeight: DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(Creatives.setSort(col, dir)),
		enableCellAutoFocus: false, /*
			This is a ReactDataGrid setting that defaults to `true`, which seems to make it fight
			with anything else trying to utilize regular `tabindex` control flow, like blade forms.
		*/
	};

	return (
		<CreativesSocketListener>
			<Measure>
				{ (obj) => {
					let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
					return (
						<div className="measure-component" ref={ obj.measureRef }>
							<ProgressIndicator isLoading={ isLoading } />
							<div ref={dataGridRef}>
								<DataGrid
									{ ...dataGridProps }
									minHeight={ gridHeight }
								/>
							</div>
						</div>
					);
				} }
			</Measure>
		</CreativesSocketListener>
	);
}

export const PRIVATE = { filterVisibleColumns };

Grid.displayName = "Grid";
